import { useLayoutEffect, useMemo, useState } from 'react';
import { GridColumnReorderEvent, GridColumnResizeEvent } from '@progress/kendo-react-grid';
import { CpsFieldForView } from '@cs-cms/types';

interface ColumnChanges {
  widths: Record<string, number>;
  order: string[];
}

interface UseCpsGridColumnsParams {
  columns: CpsFieldForView[] | undefined;
  gridRef: React.RefObject<HTMLDivElement | null>;
  selectedIndexId: number | undefined;
  selectedView: number | undefined;
}

interface UseCpsGridColumnsResult {
  columnChanges: ColumnChanges;
  gridKey: string;
  onColumnResize: (event: GridColumnResizeEvent) => void;
  onColumnReorder: (event: GridColumnReorderEvent) => void;
  resetColumnWidths: () => void;
}

export const useCpsGridColumns = (params: UseCpsGridColumnsParams): UseCpsGridColumnsResult => {
  const { columns, gridRef, selectedIndexId, selectedView } = params;

  const viewScopeKey = selectedIndexId != null && selectedView != null ? `${selectedIndexId}-${selectedView}` : null;

  const defaultColumnOrder = useMemo((): string[] => {
    if (!columns || columns.length === 0) {
      return [];
    }

    return columns.map(col => col.name);
  }, [columns]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [columnOrderOverride, setColumnOrderOverride] = useState<string[] | null>(null);
  const [trackedViewScopeKey, setTrackedViewScopeKey] = useState(viewScopeKey);

  if (viewScopeKey !== trackedViewScopeKey) {
    setTrackedViewScopeKey(viewScopeKey);
    setColumnWidths({});
    setColumnOrderOverride(null);
  }

  const columnOrder = columnOrderOverride ?? defaultColumnOrder;

  const columnChanges = useMemo(
    (): ColumnChanges => ({
      widths: columnWidths,
      order: columnOrder
    }),
    [columnWidths, columnOrder]
  );

  const [scrollPosition, setScrollPosition] = useState(0);

  useLayoutEffect(() => {
    if (scrollPosition <= 0) {
      return;
    }

    const scrollContainer = gridRef.current?.querySelector('.k-grid-content');
    if (scrollContainer) {
      scrollContainer.scrollLeft = scrollPosition;
      setScrollPosition(0);
    }
  }, [columnOrder, scrollPosition, gridRef]);

  const onColumnResize = (event: GridColumnResizeEvent): void => {
    if (!event.end) {
      return;
    }

    const { index, newWidth, columns: eventColumns } = event;

    if (typeof index !== 'number' || typeof newWidth !== 'number' || !eventColumns) {
      return;
    }

    const columnAtIndex = eventColumns[index];
    if (!columnAtIndex?.field) {
      return;
    }

    const fieldName = columnAtIndex.field;
    setColumnWidths(prev => ({
      ...prev,
      [fieldName]: newWidth
    }));
  };

  const onColumnReorder = (event: GridColumnReorderEvent): void => {
    const { columns: eventColumns } = event;
    if (!eventColumns || !columns) {
      return;
    }

    const scrollContainer = gridRef.current?.querySelector('.k-grid-content');
    if (scrollContainer) {
      setScrollPosition(scrollContainer.scrollLeft);
    }

    const sortedColumns = [...eventColumns].sort((a, b) => {
      const left = (a as { orderIndex?: number }).orderIndex ?? 0;
      const right = (b as { orderIndex?: number }).orderIndex ?? 0;
      return left - right;
    });

    const newOrder = sortedColumns.filter(col => col.field).map(col => col.field as string);
    setColumnOrderOverride(newOrder);
  };

  const resetColumnWidths = (): void => {
    setColumnWidths({});
  };

  return {
    columnChanges,
    gridKey: columnOrder.join('-'),
    onColumnResize,
    onColumnReorder,
    resetColumnWidths
  };
};
